import axios from "axios"
import {getStationCode} from "../../network/NetworkUtil";
import {getAPIGatewayDataFromParent} from "../../handler/ParentDataHandler";
import {
    calculateStageDuration,
    convertEpochMillisToLocalTime,
    convertMillisecondsToHHMM,
    convertSecondsToTimeFormat,
    formatAlias,
    getCluster,
    getDateInMMDDYYYYWithOffset,
    getLatestPicker,
    getTimeDiffFromCurrentTimeInHHMMSSFormat, isRSWSBosonFlowEnabled
} from "../../util/Util";
import {AR_V2, CORE_AMZL, DEFAULT_CAPABILITIES, STAGE_BY_TIME_THRESHOLD, TIMEOUTS} from "../../util/Constants";
import {
    getCartStatus,
    getRouteStatus,
    getStageStatus,
    handleUnassignedRoutes,
    PRE_STAGED,
    SIDELINED,
    STAGE_STATUS,
    STAGED
} from "../../util/EntityStatusManager";
import {
    CART_INFO_METRIC_KEY,
    ERROR_COUNT_METRIC, GET_STAGE_TASK_PLAN_LIST_METRIC_KEY,
    GLOBAL_METRIC_KEY,
    logErrorMetric,
    logOperationalMetric,
    MetricType,
    RSWS_ROUTE_DETAIL_METRIC,
    RSWS_ROUTE_LIST_METRIC,
    RSWS_SIDELINE_ROUTE_METRIC,
    RSWS_STATION_CONFIG_METRIC,
    RSWS_STATION_DETAILS_METRIC,
    RSWS_UNASSIGN_ROUTE_METRIC,
    STAGE_TAB_METRIC_KEY,
    SVS_FLOOR_DATA_METRIC, UPDATE_STAGE_TASK_PLAN_STATUS_METRIC_KEY
} from "../../util/MetricUtil";
import {getCartStagingLocation, getRouteStagingLocation, transformStagingLocations, getCartFloorLocation} from "../../util/DataTransformUtil";
import {Logger} from "../../util/Logger";

/** Get Route List **/
export function fetchRouteInformation(args) {
    const {capability} = args;
    if (capability === AR_V2) {
        getCartsForARStation(args);
    } else {
        getRouteInformation(args);
    }
}

async function getCartsForARStation(args) {
    try {
        const {dispatcher, filterDispatcher, baseUrl, capability, stationConfig} = args;
        const result = await Promise.all([getStationRoutes(baseUrl, capability, null, stationConfig), getFloorData()]);
        const lastUpdated = new Date().getTime()

        let routes = result[0] && result[0].status === 200 ? result[0].data.routes : [];
        let floors = result[1] && result[1].status === 200 ? result[1].data.floors : [];

        dispatcher({ type: "RECEIVE_DATA",
            data: {...getCartsFromRouteAndFloorData(routes, floors, stationConfig), lastUpdated: lastUpdated}
        });
        filterDispatcher({ type: "RECEIVE_FILTER_DATA", data: getFilterData(routes) })

    } catch (error) {
        Logger.error("Exception in StageNetworkAPI.getCartsForARStation", error);
        logErrorMetric(getStationCode(), GLOBAL_METRIC_KEY, ERROR_COUNT_METRIC);
    }
}

function getCartsFromRouteAndFloorData(routes, floors, stationConfig) {
    const routeIdToRouteMap = routes.reduce(function (map, route) {
        map[route.routeId] = route;
        return map;
    }, {});

    floors.forEach(floor => {
        const route = routeIdToRouteMap[floor.routeId];
        if (!route || !floor.cart || !floor.cart.label) {
            return;
        }
        if (route.carts.some(e => e.cartId === floor.cart.label)) {
            //cart is already staged
            const cartIndex = route.carts.findIndex(e => e.cartId === floor.cart.label);
            route.carts[cartIndex].isStowed = floor.cart.status === 'STOW_COMPLETE';
            route.carts[cartIndex].stowTime = floor.cart.stowCompleteTime;
            route.carts[cartIndex].floorLocation = floor.label;
        } else {
            //cart is not staged
            route.carts.push({
                cartId: floor.cart.label,
                isStowed: floor.cart.status === 'STOW_COMPLETE',
                stowTime: floor.cart.stowCompleteTime,
                floorLocation: floor.label
            });
        }
    });
    return extractViewModelArV2(routes, stationConfig);
}

async function getRouteInformation(args) {
    const {dispatcher, filterDispatcher, baseUrl, capability, xptNodes, stationConfig, configDispatch} = args;
    const stationCode = getStationCode();
    try {
        const response = await getStationRoutes(baseUrl, capability, xptNodes, stationConfig);
        const lastUpdated = new Date().getTime()
        if (response) {
            const output = response.data.routes;
            dispatcher({
                type: "UPDATE_ROUTE_DATA_CACHE",
                data: {
                    record: {response: response.data, xptNodes: xptNodes, stationCode: stationCode},
                    lastUpdated: lastUpdated
                }
            });
            dispatcher({type: "RECEIVE_DATA",
                data: {...getRouteData(output, capability, stationConfig), lastUpdated: lastUpdated}
            });
            filterDispatcher({type: "RECEIVE_FILTER_DATA", data: getFilterData(output)})
            configDispatch({
                type: 'RECEIVE_REFRESH_INTERVAL',
                data: {
                    refreshInterval:  response.data.routeRefreshRate
                }
            });
        }
    } catch (error) {
        Logger.error("Exception in StageNetworkAPI.getRouteInformation", error);
        logErrorMetric(getStationCode(), GLOBAL_METRIC_KEY, ERROR_COUNT_METRIC);
    }
}

async function getStationRoutes(baseUrl, capability, xptNodes, stationConfig) {
    const startTime = Date.now();
    const stationCode = getStationCode();
    try {
        const api = axios.create({
            baseURL: baseUrl,
            timeout: TIMEOUTS.RSWS_GET_STATION_ROUTES_API_TIMEOUT
        });
        var response;
        if(stationConfig && stationConfig.authEnabled) {
            const requestPayload = getRouteRequestPayload(capability, xptNodes);
            response = await getAPIGatewayDataFromParent(requestPayload);
        } else {
            response = await api.get(getRouteRequestURL(capability, xptNodes));
        }
        if (response && response.status === 200) {
            logOperationalMetric(startTime, stationCode, STAGE_TAB_METRIC_KEY, RSWS_ROUTE_LIST_METRIC, MetricType.SUCCESS);
            return response;
        } else {
            var error = {
                response : response
            }
            Logger.error("Exception in StageNetworkAPI.getStationRoutes" , error);
            logOperationalMetric(startTime, stationCode, STAGE_TAB_METRIC_KEY, RSWS_ROUTE_LIST_METRIC, MetricType.FAILED, error);
        }
    } catch (e) {
        Logger.error("Exception in StageNetworkAPI.getStationRoutes", e);
        logOperationalMetric(startTime, stationCode, STAGE_TAB_METRIC_KEY, RSWS_ROUTE_LIST_METRIC, MetricType.FAILED, e);
    }
    return null;
}

function getRouteRequestPayload(capability, xptNodes) {
    const mainUri = `/apigw/route/station/${getStationCode()}`;
    if (DEFAULT_CAPABILITIES.includes(capability) && Array.isArray(xptNodes) && xptNodes.length) {
        const nodeList = [...xptNodes];
        const requestBody = {
            "date": [getDateInMMDDYYYYWithOffset(0)],
            "nodes": [nodeList.join(",")]
        }
        const requestPayload = {
            resourcePath: mainUri,
            httpMethod: "get",
            processName: "routeStaging",
            requestParams: requestBody
        }
        return requestPayload;
    } else {
        const requestBody = {
            "date": [getDateInMMDDYYYYWithOffset(0)]
        }
        const requestPayload = {
            resourcePath: mainUri,
            httpMethod: "get",
            processName: "routeStaging",
            requestParams: requestBody
        }
        return requestPayload;
    }
}

function getRouteRequestURL(capability, xptNodes) {
    const mainUri = `/rsws/route/station/${getStationCode()}?date=${getDateInMMDDYYYYWithOffset(0)}`;
    if (DEFAULT_CAPABILITIES.includes(capability) && Array.isArray(xptNodes) && xptNodes.length) {
        const nodeList = [...xptNodes];
        return `${mainUri}&nodes=${nodeList.join(",")}`;
    } else {
        return mainUri;
    }
}

async function getFloorData() {
    const startTime = Date.now();
    const stationCode = getStationCode();
    try {
        const filterMap = {
            'NODE': [stationCode]
        }
        const requestBody = {
            filters: filterMap,
            fieldsRequired: ["FLOOR_LABEL", "ROUTE_CODE", "CART_DATA", "ROUTE_ID"]
        }
        const requestPayload = {
            resourcePath: "svs/floors/data",
            httpMethod: "post",
            processName: "stow",
            requestBody: requestBody
        }
        const response = await getAPIGatewayDataFromParent(requestPayload);
        if (response && response.status === 200) {
            logOperationalMetric(startTime, stationCode, STAGE_TAB_METRIC_KEY, SVS_FLOOR_DATA_METRIC, MetricType.SUCCESS);
            return response;
        } else {
            logOperationalMetric(startTime, stationCode, STAGE_TAB_METRIC_KEY, SVS_FLOOR_DATA_METRIC, MetricType.FAILED);
        }

    } catch (e) {
        Logger.error("Exception in StageNetworkAPI.getFloorData", e);
        logOperationalMetric(startTime, stationCode, STAGE_TAB_METRIC_KEY, SVS_FLOOR_DATA_METRIC, MetricType.FAILED);
    }
    return null;
}

export function getStageTaskPlanList(baseUrl, stationConfig, dispatcher) {
    getStageTaskPlanListContent(baseUrl, stationConfig, dispatcher)
}

async function getStageTaskPlanListContent(baseUrl, stationConfig, dispatcher) {
    const startTime = Date.now();
    const stationCode = getStationCode()
    const mainUri = `/apigw/getStageTasks/stationCode/${stationCode}`;
    try {
        const api = axios.create({
            baseURL: baseUrl,
            timeout: TIMEOUTS.RSWS_DEFAULT_API_TIMEOUT
        });
        var response;
        const requestBody = {
            "associateId": "DUMMY_ID"
        }
        if (stationConfig && stationConfig.authEnabled) {
            const requestPayload = {
                resourcePath: mainUri,
                httpMethod: "POST",
                processName: "routeStaging",
                requestBody: requestBody
            }
            response = await getAPIGatewayDataFromParent(requestPayload);
        } else {
            response = await api.post(mainUri, requestBody);
        }
        if (response && response.status === 200) {
            logOperationalMetric(startTime, stationCode, GET_STAGE_TASK_PLAN_LIST_METRIC_KEY, RSWS_ROUTE_DETAIL_METRIC, MetricType.SUCCESS);
            const output = {
                stageTaskPlanList: response.data.stageTaskList
            };
            dispatcher({type: "RECEIVE_DATA", data: output});
        } else {
            var error = {
                response : response
            }
            Logger.error("Exception in StageNetworkAPI.getStageTaskPlanList" , error);
            logOperationalMetric(startTime, stationCode, GET_STAGE_TASK_PLAN_LIST_METRIC_KEY, RSWS_ROUTE_DETAIL_METRIC, MetricType.FAILED, error);
        }
    } catch (error) {
        Logger.error("Exception in StageNetworkAPI.getStageTaskPlanList", error);
        logOperationalMetric(startTime, stationCode, GET_STAGE_TASK_PLAN_LIST_METRIC_KEY, RSWS_ROUTE_DETAIL_METRIC, MetricType.FAILED, error);
    }
}

export function updateStageTaskPlanStatus(baseUrl, stationConfig, dispatcher, planId, status, forceUpdate, cycleName) {
    updateStageTaskPlanStatusContent(baseUrl, stationConfig, dispatcher, planId, status, forceUpdate, cycleName)
}

async function updateStageTaskPlanStatusContent(baseUrl, stationConfig, dispatcher, planId, status, forceUpdate, cycleName) {
    const startTime = Date.now();
    const stationCode = getStationCode()
    const baseUri = `/apigw/updateStageTaskPlan/stationCode/${stationCode}/planId/${planId}/status/${status}`;
    const mainUri = `${baseUri}?forceUpdate=${forceUpdate}&cycleName=${cycleName}`;
    try {
        const api = axios.create({
            baseURL: baseUrl,
            timeout: TIMEOUTS.RSWS_DEFAULT_API_TIMEOUT
        });
        var response;
        const requestBody = {
            "associateId": "DUMMY_ID"
        }
        if (stationConfig && stationConfig.authEnabled) {
            const requestPayload = {
                resourcePath: baseUri,
                httpMethod: "POST",
                processName: "routeStaging",
                requestBody: requestBody,
                requestParams: {
                    forceUpdate: [forceUpdate.toString()],
                    cycleName: [cycleName.toString()]
                }
            }
            response = await getAPIGatewayDataFromParent(requestPayload);
        } else {
            response = await api.post(mainUri, requestBody);
        }

        if (response && response.status === 200) {
            // Check for pending route adjustments only when forceUpdate=false
            if (forceUpdate === false && response.data) {
                const { stageBlockingTasksPending = false, countOfPendingTasks = 0 } = response.data || {};
                if (stageBlockingTasksPending === true) {
                    dispatcher({
                        type: "SHOW_ROUTE_ADJUSTMENT_PENDING",
                        data: {
                            planId: planId,
                            status: status,
                            numberOfRemainingPackages: countOfPendingTasks
                        }
                    });
                    logOperationalMetric(startTime, stationCode, UPDATE_STAGE_TASK_PLAN_STATUS_METRIC_KEY, RSWS_ROUTE_DETAIL_METRIC, MetricType.SUCCESS);
                    return;
                }
            }

            logOperationalMetric(startTime, stationCode, UPDATE_STAGE_TASK_PLAN_STATUS_METRIC_KEY, RSWS_ROUTE_DETAIL_METRIC, MetricType.SUCCESS);
            getStageTaskPlanListContent(baseUrl, stationConfig, dispatcher)
        } else {
            var error = {
                response : response
            }
            Logger.error("Exception in StageNetworkAPI. updateStageTaskPlanStatus" , error);
            logOperationalMetric(startTime, stationCode, UPDATE_STAGE_TASK_PLAN_STATUS_METRIC_KEY, RSWS_ROUTE_DETAIL_METRIC, MetricType.FAILED, error);
        }
    } catch (error) {
        Logger.error("Exception in StageNetworkAPI. updateStageTaskPlanStatus", error);
        logOperationalMetric(startTime, stationCode, UPDATE_STAGE_TASK_PLAN_STATUS_METRIC_KEY, RSWS_ROUTE_DETAIL_METRIC, MetricType.FAILED, error);
    }
}

export function getRouteData(output, capability, stationConfig) {
    const model = {
        totalRoutes: '0',
        stagedRoutes: '0',
        preStagedCarts: '0',
        stagedCarts: '0',
        dwellingCartsDSP: '0',
        dwellingCartsFlex: '0',
        cartList: []
    }
    output = handleUnassignedRoutes(capability, output);
    model.routeList = convertRouteToUIModel(output, capability, stationConfig);
    model.totalRoutes = model.routeList.length;

    model.routeList.forEach(route => {
        if (route.routeStatus === "Staged"
            || route.routeStatus === "Departed"
            || route.routeStatus === "Departed (partial)") {
            model.stagedRoutes++;
        }
        const carts = route.carts;
        const currentRouteCarts = [];
        carts.forEach(cart => {
            const cartDetail = {
                routeId: route.routeId,
                cartId: cart.cartId,
                stationCode: route.stationCode,
                routeCode: route.routeCode,
                routeType: route.routeType,
                routeSLA: convertMillisecondsToHHMM(route.criticalPullTime),
                status: getCartStatus(route, cart, capability, stationConfig),
                routeStatus: route.sidelined ? SIDELINED : route.routeStatus,
                location: getCartStagingLocation(route, cart, capability),
                routeStagingLocation: route.stagingLocation,                  /* this is sent to cart drill down view */
                hazmatWeight: route.hazmatWeight ? route.hazmatWeight : "...",
                picker: route.picker,
                associate: cart.assignee ?
                    cart.assignee.indexOf("@") > -1 ? cart.assignee.split("@")[0] : cart.assignee
                    : "...",
                driver: route.driver ? route.driver.name : "...",
                dwelling: cart.isDwelling,
                stageTime: cart.stageTime,
                dwellingDuration: cart.isDwelling && cart.dwellDuration ?
                    convertSecondsToTimeFormat(cart.dwellDuration / 1000) : "..."
            }
            model.cartList.push(cartDetail)
            currentRouteCarts.push(cartDetail)

            if (cartDetail.status === STAGED) {
                model.stagedCarts++;
            } else if (cartDetail.status === PRE_STAGED) {
                model.preStagedCarts++;
            }
        })
        route.carts = currentRouteCarts;
    })
    model.dwellingCartsDSP = model.cartList.filter(cart => ((cart.dwelling === true) && cart.routeType.startsWith("DSP"))).length;
    model.dwellingCartsFlex = model.cartList.filter(cart => ((cart.dwelling === true) && !cart.routeType.startsWith("DSP"))).length;
    return model;
}

function convertRouteToUIModel(routes, capability, stationConfig) {
    return routes.map(route => {
        if (capability === CORE_AMZL) {
            route.carts = route.carts.filter(cart => {
                return (cart.preStageLocation !== null && cart.preStageLocation !== '') || cart.stageTime;
            })
        }
        route.criticalPullTime = getDispatchTime(route, capability);
        let stageStatus = getStageStatus(route, stationConfig, capability);
        route.stagingLocations = transformStagingLocations(route);
        return {
            routeId: route.routeId,
            routeCode: route.routeCode,
            stationCode: route.stationCode,
            routeType: route.type,
            routeSLA: convertMillisecondsToHHMM(route.criticalPullTime),
            criticalPullTime: route.criticalPullTime,
            plannedDispatchTime: route.plannedDispatchTime,
            routeStatus: getRouteStatus(route, stationConfig),
            picker: formatAlias(getLatestPicker(capability, route)),
            stageStatus: stageStatus,   /* NOT USED FOR ARV2 */
            hazmatWeight: route.hazmatWeight ? route.hazmatWeight : "",
            driver: route.driver ? route.driver.name : "...",
            sidelined: route.sidelined,
            stagingLocations: route.stagingLocations,               // Represents list of staging location objects
            stagingLocation: getRouteStagingLocation(route),        // Represents display label for staging location
            totalCarts: route.carts ? route.carts.length : 0,
            carts: route.carts,
            stageTime: getRouteStageTime(route, stageStatus),
            stageProgress: getStageProgress(route),
            startTime: getRouteStartTime(route),                                    /* first picker start time */
            endTime: getRouteEndTime(route),
            stageDuration: calculateStageDuration(route),  /* overall picking duration */
            stageError:route.pickingErrorMessage,
            stationPairs: getStationPairs(route)
        }
    });
}

function getRouteStartTime(route){
    if(route.pickAssociates.length && route.pickAssociates[0].startTime) {
        return convertEpochMillisToLocalTime(route.pickAssociates[0].startTime)
    }
    return "...";
}

function getRouteEndTime(route){
    if(route.pickAssociates.length && route.pickAssociates[0].endTime) {
        return convertEpochMillisToLocalTime(route.pickAssociates[0].endTime)
    }
    return "...";
}

function getStageProgress(rswsRoute) {
    if (rswsRoute.stageProgress) {
        return rswsRoute.stageProgress.completedCount + "/" + rswsRoute.stageProgress.totalCount;
    }
    return "...";
}

function getRouteStageTime(route, stageStatus) {
    if (Array.isArray(route.carts) && route.carts.length
        && (stageStatus === STAGE_STATUS.STAGED || stageStatus === STAGE_STATUS.PARTIALLY_STAGED)
        && !route.sidelined) {

        const cartList = route.carts.filter(cart => cart.stageTime);
        if (cartList.length) {
            const lastCart = cartList.reduce((previous, current) => (previous.stageTime > current.stageTime) ? previous : current);
            return lastCart.stageTime;
        }
    }
    return null;
}

function getStationPairs(route) {
    if (route.sourceLocation != null && route.sourceLocation.length > 0) {
        return route.sourceLocation.filter(function (item, pos) {
            return route.sourceLocation.indexOf(item) === pos;
        }).join(", ");
    }
    return "...";
}

/**
 * return driver arrival time if available else fallback to RTW dispatch time.
 * @param route
 * @param capability
 * @return {*}
 */
export function getDispatchTime(route, capability){
    if (capability === CORE_AMZL && route.plannedDispatchTime) {
        return route.plannedDispatchTime;
    }
    return route.criticalPullTime;
}

function extractViewModelArV2(routes, stationConfig) {
    const model = {
        totalRoutes: '0',
        stagedRoutes: '0',
        departedRoutes: '0',
        preStagedCarts: '0',
        stagedCarts: '0',
        dwellingCartsDSP: '0',
        dwellingCartsFlex: '0',
        routeList: [],
        cartList: []
    }

    model.routeList = convertRouteToUIModel(routes, AR_V2, stationConfig);

    model.routeList.forEach(route => {
        const carts = route.carts;
        carts.forEach(cart => {
            const cartDetail = {
                ...route,
                cartId: cart.cartId,
                stageListId: cart.stageListId,
                stationCode: route.stationCode,
                stageSLA: calculateStageSLA(route.criticalPullTime),
                status: getCartStatus(route, cart, AR_V2, stationConfig),
                location: getCartStagingLocation(route, cart, AR_V2),
                routeStagingLocation: route.stagingLocation, /* this is sent to cart drill down view */
                associateId: cart.assignee,
                floorLocation: getCartFloorLocation(cart, AR_V2),
                associate: cart.assignee ?
                    cart.assignee.indexOf("@") > -1 ? cart.assignee.split("@")[0] : cart.assignee
                    : "...",
            }

            cartDetail.dwelling = cartDetail.status === "Ready" && cart.isStowed ? "Yes" : "NO";
            cartDetail.dwellingDuration = cartDetail.dwelling && cart.stowTime ?
                getTimeDiffFromCurrentTimeInHHMMSSFormat(cart.stowTime)
                : "...";

            //This will fix the dwelling duration for the hotswapped carts in case they are removed from sort location
            if(cartDetail.dwellingDuration === "..." && cart.isDwelling && cart.dwellDuration) {
                cartDetail.dwellingDuration = convertSecondsToTimeFormat(cart.dwellDuration/1000);
                cartDetail.dwelling = "Yes";
            }

            model.cartList.push(cartDetail)
            if (cartDetail.status === "Staged") {
                model.stagedCarts++;
            }
        });
        if (route.routeStatus === "Staged") {
            model.stagedRoutes++;
        }
        else if (route.routeStatus === "Departed") {
            model.departedRoutes++;
        }
    })
    model.totalRoutes = model.routeList.length;
    model.dwellingCartsDSP = model.cartList.filter(cart => (cart.dwelling === "Yes" && cart.routeType.startsWith("DSP"))).length;
    model.dwellingCartsFlex = model.cartList.filter(cart => (cart.dwelling === "Yes" && !cart.routeType.startsWith("DSP"))).length;
    return model;
}

function calculateStageSLA(criticalPullTime) {
    if (criticalPullTime) {
        const stageByTime = criticalPullTime - (STAGE_BY_TIME_THRESHOLD);
        return convertMillisecondsToHHMM(stageByTime);
    } else {
        return "...";
    }
}

export function getFilterData(output) {
    const data = {
        departure: new Set(),
        driverType: new Set(["DSP", "DP"]),
        cluster: new Set(),
    }
    output.forEach(route => {
        if (route.type !== "...") {
            data.driverType.add(route.type)
        }
        data.cluster.add(getCluster(route.routeCode));
        if (route.criticalPullTime != null) {
            data.departure.add(convertMillisecondsToHHMM(route.criticalPullTime));
        }
    })
    return data;
}

/** Get Route Information for give route code **/
export function fetchRouteContent(routeId, cartId, dispatcher, baseUrl, stationCode, stationConfig) {
    getRouteContent(routeId, cartId, dispatcher, baseUrl, stationCode, stationConfig)
}

async function getRouteContent(routeId, cartId, dispatcher, baseUrl, stationCode, stationConfig) {
    const startTime = Date.now();
    try {
        const api = axios.create({
            baseURL: baseUrl,
            timeout: TIMEOUTS.RSWS_DEFAULT_API_TIMEOUT
        });
        var response;
        if(stationConfig && stationConfig.authEnabled) {
            const requestBody = {
                "date": [getDateInMMDDYYYYWithOffset(0)]
            }
            const requestPayload = {
                resourcePath: "/apigw/route/"+routeId+"/stationCode/" + stationCode,
                httpMethod: "get",
                processName: "routeStaging",
                requestParams: requestBody
            }
            response = await getAPIGatewayDataFromParent(requestPayload);
        } else {
            response = await api.get("/rsws/route/"+routeId+"/stationCode/" + stationCode + "?date="+ getDateInMMDDYYYYWithOffset(0));
        }
        if (response && response.status === 200) {
            logOperationalMetric(startTime, stationCode, CART_INFO_METRIC_KEY, RSWS_ROUTE_DETAIL_METRIC, MetricType.SUCCESS);
            const output = response.data.containers;
            dispatcher({type: "RECEIVE_DATA", data: getContainers(output, cartId)});
        } else {
            var error = {
                response : response
            }
            Logger.error("Exception in StageNetworkAPI.getRouteContent" , error);
            logOperationalMetric(startTime, stationCode, CART_INFO_METRIC_KEY, RSWS_ROUTE_DETAIL_METRIC, MetricType.FAILED, error);
        }
    } catch (error) {
        Logger.error("Exception in StageNetworkAPI.getRouteContent", error);
        logOperationalMetric(startTime, stationCode, CART_INFO_METRIC_KEY, RSWS_ROUTE_DETAIL_METRIC, MetricType.FAILED, error);
    }
}

function getContainers(output, cartId) {
    const model = {
        name: cartId,
        bags: [],
        packages: []
    }
    const containers = output.filter(container => container.parentResourceLabel === cartId);
    containers.forEach(container => {
        if (container.containerType === "PACKAGE") {
            model.packages.push(getContainer(container))
        } else {
            model.bags.push(getContainer(container))
        }
    })
    return model;
}

function getContainer(container) {
    return {
        label: container.label,
        location: container.stackingLocation,
        picked: container.containerStates.includes("PICKED"),
        staged: container.containerStates.includes("STAGED"),
        departed: container.containerStates.includes("DEPARTED")
    }
}

/** SideLine Route **/
export function sideLineRoute(dispatcher, routeId, stationCode, baseUrl, stationConfig) {
    sidelineRouteRequest(dispatcher, routeId, stationCode, baseUrl, stationConfig)
}

async function sidelineRouteRequest(dispatcher, routeId, stationCode, baseUrl, stationConfig) {
    const startTime = Date.now();
    try {
        const requestBody = {
            "stationCode": stationCode,
            "operationType": "SIDELINE",
            "requestAttributes" : {
                "routeId": [routeId]
            }
        }
        const api = axios.create({
            baseURL: baseUrl,
            timeout: TIMEOUTS.RSWS_DEFAULT_API_TIMEOUT
        });
        var response;
        if(stationConfig && stationConfig.authEnabled) {
            const requestPayload = {
                resourcePath: "/apigw/execute-operation/",
                httpMethod: "post",
                processName: "routeStaging",
                requestBody: requestBody
            }
           response = await getAPIGatewayDataFromParent(requestPayload);
        } else {
            response = await api.post("/rsws/execute-operation/", requestBody);
        }
        if (response && response.status === 200) {
            logOperationalMetric(startTime, stationCode, STAGE_TAB_METRIC_KEY, RSWS_SIDELINE_ROUTE_METRIC, MetricType.SUCCESS);
            dispatcher({type: "ROUTE_SIDELINED_SUCCESS", data:{
                    alert: {title: "Route Sidelined", type: "success"}}
            });
        } else {
            var error = {
                response : response
            }
            Logger.error("Exception in StageNetworkAPI.sidelineRouteRequest" , error);
            logOperationalMetric(startTime, stationCode, STAGE_TAB_METRIC_KEY, RSWS_SIDELINE_ROUTE_METRIC, MetricType.FAILED, error);
            dispatcher({type: "ROUTE_SIDELINED_ERROR", data:{
                    alert: {title: "Route Sidelined Failed", type: "error"}}
            });
        }
    } catch (error) {
        Logger.error("Exception in StageNetworkAPI.sidelineRouteRequest", error);
        logOperationalMetric(startTime, stationCode, STAGE_TAB_METRIC_KEY, RSWS_SIDELINE_ROUTE_METRIC, MetricType.FAILED, error);
        dispatcher({type: "ROUTE_SIDELINED_ERROR", data:{
            alert: {title: "Route Sidelined Failed", type: "error"}}
        });
    }
}

/** Unassign Stager **/
export function unassignStager(dispatcher, stageListId, stationCode, associateId, baseUrl, stationConfig) {
    unassignStagerRequest(dispatcher, stageListId, stationCode, associateId, baseUrl, stationConfig)
}

async function unassignStagerRequest(dispatcher, stageListId, stationCode, associateId, baseUrl, stationConfig) {
    const startTime = Date.now();
    try {
        const requestBody = {
            "stationCode": stationCode,
            "operationType": "UNASSIGN_STAGER",
            "requestAttributes": {
                "stagelistId": [stageListId],
                "associateId": [associateId]
            }
        }
        const api = axios.create({
            baseURL: baseUrl,
            timeout: TIMEOUTS.RSWS_DEFAULT_API_TIMEOUT
        });
        var response;
        if(stationConfig && stationConfig.authEnabled) {
            const requestPayload = {
                resourcePath: "/apigw/execute-operation/",
                httpMethod: "post",
                processName: "routeStaging",
                requestBody: requestBody
            }
            response = await getAPIGatewayDataFromParent(requestPayload);
        } else {
            response = await api.post("/rsws/execute-operation/", requestBody);
        }
        if (response && response.status === 200) {
            logOperationalMetric(startTime, stationCode, STAGE_TAB_METRIC_KEY, RSWS_UNASSIGN_ROUTE_METRIC, MetricType.SUCCESS);
            dispatcher({
                type: "SHOW_NOTIFICATION",
                data: {
                    alert: {title: "Cart Unassigned", type: "success"}
                }
            });
        } else {
            var error = {
                response : response
            }
            Logger.error("Exception in StageNetworkAPI.unassignStagerRequest" , error);
            logOperationalMetric(startTime, stationCode, STAGE_TAB_METRIC_KEY, RSWS_UNASSIGN_ROUTE_METRIC, MetricType.FAILED, error);
            dispatcher({
                type: "SHOW_NOTIFICATION",
                data: {
                    alert: {title: "Cart Unassignment Failed", type: "error"}
                }
            });
        }

    } catch (error) {
        Logger.error("Exception in StageNetworkAPI.unassignStagerRequest", error);
        logOperationalMetric(startTime, stationCode, STAGE_TAB_METRIC_KEY, RSWS_UNASSIGN_ROUTE_METRIC, MetricType.FAILED, error);
        dispatcher({
            type: "SHOW_NOTIFICATION",
            data: {
                alert: {title: "Cart Unassignment Failed", type: "error"}
            }
        });
    }
}

/** Get Station Config **/
export function fetchNodeConfig(dispatcher, baseUrl) {
    getNodeConfig(dispatcher, baseUrl);
}

async function getNodeConfig(dispatcher, baseUrl) {
    const startTime = Date.now();
    const stationCode = getStationCode();
    try {
        let response;
        if (await isRSWSBosonFlowEnabled()) {
            const requestPayload = {
                resourcePath: "/apigw/config",
                requestParams: {stationCode: [stationCode]},
                httpMethod: "get",
                processName: "routeStaging"
            }
            response = await getAPIGatewayDataFromParent(requestPayload);
        } else {
            const api = axios.create({
                baseURL: baseUrl,
                timeout: TIMEOUTS.RSWS_DEFAULT_API_TIMEOUT
            });
            response = await api.get("/rsws/config?stationCode=" + stationCode);
        }
        if (response && response.status === 200) {
            logOperationalMetric(startTime, stationCode, STAGE_TAB_METRIC_KEY, RSWS_STATION_CONFIG_METRIC, MetricType.SUCCESS);
            const stationConfig = response.data;
            dispatcher({
                type: "RECEIVE_STATION_CONFIG",
                data: {
                    stationConfig: {
                        initialised: true,
                        ...stationConfig,
                        refreshInterval: stationConfig.routeRefreshRate
                    }
                }
            });
        } else {
            var error = {
                response : response
            }
            Logger.error("Exception in StageNetworkAPI.getNodeConfig" , error);
            logOperationalMetric(startTime, stationCode, STAGE_TAB_METRIC_KEY, RSWS_STATION_CONFIG_METRIC, MetricType.FAILED, error);
        }
    } catch (e) {
        Logger.error("Exception in StageNetworkAPI.getNodeConfig", e);
        logOperationalMetric(startTime, stationCode, STAGE_TAB_METRIC_KEY, RSWS_STATION_CONFIG_METRIC, MetricType.FAILED, e);
    }
    return null;
}

/** Get XPT Stations **/
export function fetchSubStations(dispatcher, baseUrl, capability, metricKey, stationConfig) {
    if (DEFAULT_CAPABILITIES.includes(capability)) {
        getSubStationsWithParentNode(dispatcher, baseUrl, metricKey, stationConfig);
    }
}

async function getSubStationsWithParentNode(dispatcher, baseUrl, metricKey, stationConfig) {
    const startTime = Date.now();
    const stationCode = getStationCode();
    try {
        const api = axios.create({
            baseURL: baseUrl,
            timeout: TIMEOUTS.RSWS_DEFAULT_API_TIMEOUT
        });
        var response;
        if(stationConfig && stationConfig.authEnabled) {
            const requestPayload = {
                resourcePath: "/apigw/details/station/" + stationCode,
                httpMethod: "get",
                processName: "routeStaging"
            }
            response = await getAPIGatewayDataFromParent(requestPayload);
        } else {
            response = await api.get("/rsws/details/station/" + stationCode);
        }
        if (response && response.status === 200) {
            logOperationalMetric(startTime, stationCode, metricKey, RSWS_STATION_DETAILS_METRIC, MetricType.SUCCESS);
            const subStationsResponse = response.data;
            const xptNodeList = parseSubStationsResponse(subStationsResponse);
            if (xptNodeList && Array.isArray(xptNodeList) && xptNodeList.length > 0) {
                xptNodeList.push(stationCode);
            }
            dispatcher({
                type: "RECEIVE_XPT_NODES", data: {xptNodes: xptNodeList}
            });
        } else {
            var error = {
                response : response
            }
            Logger.error("Exception in StageNetworkAPI.getSubStations" , error);
            logOperationalMetric(startTime, stationCode, metricKey, RSWS_STATION_DETAILS_METRIC, MetricType.FAILED, error);
        }

    } catch (e) {
        Logger.error("Exception in StageNetworkAPI.getSubStations", e);
        logOperationalMetric(startTime, stationCode, metricKey, RSWS_STATION_DETAILS_METRIC, MetricType.FAILED, e);
    }
    return null;
}

function parseSubStationsResponse(response) {
    if (response && Array.isArray(response.subNodes)) {
        return response.subNodes.map(station => {
            return station.code;
        }, []);
    }
    return [];
}



// WEBPACK FOOTER //
// ./src/components/stageStatus/network/StageNetworkAPI.js